"use client";

import { SplitText } from "@/mono/lib/gsap";
import useIsomorphicLayoutEffect from "@/mono/lib/hooks/useIsomorphicLayoutEffect";
import { useTicker } from "@/mono/lib/hooks/useRaf";
import useResize from "@/mono/lib/hooks/useResize";
import { useScrollRigScrollTrigger } from "@/mono/lib/hooks/useScrollRigScrollTrigger";
import { clamp, lerp, map } from "@/mono/lib/math";
import { setTypography } from "@/mono/lib/styles/mixins";
import { useGlobalStore } from "@/mono/store/global";
import {
  useScrollbar,
  useTracker,
} from "@/mono/third-party/r3f-scroll-rig-custom";
import { useEffect, useRef } from "react";
import styled from "styled-components";

import { delta } from "@/mono/lib/hooks/useRaf";

function easeOutExpo(x) {
  return x === 1 ? 1 : 1 - Math.pow(2, -10 * x);
}

// const TYPE = "words";
const TYPE = "chars";

const RANGE_START = 0.2;
const RANGE_END = 0.6;

// TODO: Can be easier with splittext propIndex

export function ScrollingText({ children, offset, maxWidth = "100%" }) {
  const el = useRef();
  const tracker = useTracker(el);
  const { onScroll } = useScrollbar();
  const { scrollState, rect } = tracker;

  const splitText = useRef();

  const wordsPositions = useRef([]);

  // For clamping progress :)
  const prevProgress = useRef(0);

  useScrollRigScrollTrigger({
    el,
    offset,
    lerpAlpha: 0.05,
    update: (v, scrollState) => {
      const lastWord =
        wordsPositions.current[wordsPositions.current.length - 1];
      if (!lastWord || !el.current) return;

      let scrollProgress = map(v, RANGE_START, RANGE_END, 0, 1, true);

      const speed = 0.8;
      const maxDelta = delta.getState().d * speed;
      // HINT: Max lerp speed, limit how fast the progress can change per frame

      const d = scrollProgress - prevProgress.current;
      const clampedDelta = clamp(d, -maxDelta, maxDelta);
      scrollProgress = prevProgress.current + clampedDelta;

      prevProgress.current = scrollProgress;
      el?.current?.style?.setProperty?.("--scroll-progress", scrollProgress);
    },
  });

  function revert() {
    splitText.current?.revert();
    splitText.current = null;
  }

  function calculatePosition(word, index, totalWords) {
    const bounds = word.getBoundingClientRect();
    const parentBounds = el.current.getBoundingClientRect();

    const totalHeight = parentBounds.height;
    const lineHeight = bounds.height;
    const numLines = Math.ceil(totalHeight / lineHeight);
    const lineWidth = parentBounds.width;

    const lineNumber = Math.floor((bounds.top - parentBounds.top) / lineHeight);
    const linePosition = (bounds.left - parentBounds.left) / lineWidth;

    // Calculate raw position (0-1)
    const rawPosition = (lineNumber + linePosition) / numLines;

    // Adjust the remapping to ensure the last word's position is closer to the start
    const START_OFFSET = 0;
    const END_OFFSET = 0.8; // Add this to compress the range
    const remappedPosition =
      START_OFFSET + (END_OFFSET - START_OFFSET) * rawPosition;

    return {
      position: clamp(remappedPosition, 0, END_OFFSET),
    };
  }

  useResize(
    () => {
      if (splitText.current) {
        revert();
      }

      splitText.current = new SplitText(el.current, {
        // HINT: When splitting on both, it will correctly split line breaks
        type: "words, chars",
      });

      wordsPositions.current = splitText.current?.[TYPE].map(calculatePosition);
      splitText.current?.[TYPE].forEach((word, i) => {
        word.classList.add("scrolling-text-word");
        const pos = wordsPositions.current[i].position;
        word.style.setProperty("--word-pos", pos);
      });
    },
    { immediate: true, debounce: 100 }
  );

  useEffect(() => {
    return () => {
      if (el.current) el?.current?.style?.setProperty?.("--scroll-progress", 0);
      revert();
    };
  }, []);

  return (
    <StyledScrollingText
      className="scrolling-text"
      ref={el}
      $maxWidth={maxWidth}
    >
      {children}
    </StyledScrollingText>
  );
}

const StyledScrollingText = styled.h3`
  color: ${({ theme }) => theme.theme.textColor};
  max-width: ${({ $maxWidth }) => $maxWidth};
  ${setTypography("heading3")};

  /* Increase the fade window to ensure complete fade */
  --fade-window: 0.2;

  .scrolling-text-word {
    --value: clamp(
      0.05,
      calc(
        (var(--scroll-progress) - var(--word-pos)) * (1 / var(--fade-window))
      ),
      1
    );
    opacity: var(--value);
  }
`;
